// src/app/services/map.service.ts
import { Injectable } from '@angular/core';
import { environment } from '@qwyk/portals/environment';

declare let google: any;
declare let mapboxgl: any;

@Injectable({
    providedIn: 'root',
})
export class MapService {
    private provider: 'google' | 'mapbox' | null = null;
    private mapInstance: any = null;
    private containerId: string | null = null;
    private infoWindow: any = null;
    private googleMapsPromise: Promise<void> | null = null;
    private googleApi: any = null;

    private mapboxLayers: Set<string> = new Set();
    private mapboxMarkers: any[] = [];

    async initMap(containerId: string, options: any): Promise<void> {
        const isChina = await this.isUserInChina();
        this.containerId = containerId;

        if (isChina) {
            const enableMapbox = options.enableMapbox ?? true;

            if (!enableMapbox) {
                return;
            }

            this.provider = 'mapbox';
            await this.loadScript('https://api.mapbox.com/mapbox-gl-js/v3.18.0/mapbox-gl.js');
            await this.loadStylesheet('https://api.mapbox.com/mapbox-gl-js/v3.18.0/mapbox-gl.css');

            mapboxgl.accessToken = environment.utils.maps.mapbox.apiKey;

            return new Promise<void>(resolve => {
                const container = document.getElementById(containerId);

                if (this.mapInstance) {
                    this.clearMapboxContent();

                    const currentContainer = this.mapInstance.getContainer();
                    if (currentContainer && currentContainer.parentNode) {
                        currentContainer.parentNode.removeChild(currentContainer);
                    }

                    if (container) {
                        container.appendChild(this.mapInstance.getContainer());
                    }

                    this.mapInstance.setCenter([options.center.lng, options.center.lat]);
                    this.mapInstance.setZoom(options.zoom || 12);

                    this.mapInstance.resize();

                    resolve();
                } else {
                    this.mapInstance = new mapboxgl.Map({
                        container: containerId,
                        style: 'mapbox://styles/mapbox/light-v10', // Use light style as base for easier customization
                        center: [options.center.lng, options.center.lat],
                        zoom: options.zoom || 12,
                    });

                    this.mapInstance.on('load', () => {
                        if (options.themeColors) {
                            this.applyMapboxTheme(options.themeColors);
                        }
                        resolve();
                    });
                }
            });
        } else {
            this.provider = 'google';

            const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${environment.utils.maps.google.apiKey}&libraries=marker&loading=async`;
            await this.loadScript(scriptUrl, true);

            const mapOptions = { ...options };

            if ('enableMapbox' in mapOptions) {
                delete mapOptions.enableMapbox;
            }
            if ('themeColors' in mapOptions) {
                delete mapOptions.themeColors;
            }

            const currentContainer = document.getElementById(containerId);
            if (this.mapInstance && this.mapInstance.getDiv() === currentContainer) {
                this.mapInstance.setOptions(mapOptions);
                if (options.center) {
                    this.mapInstance.setCenter(options.center);
                }
                if (options.zoom) {
                    this.mapInstance.setZoom(options.zoom);
                }
            } else {
                this.mapInstance = new this.googleApi.maps.Map(document.getElementById(containerId), mapOptions);
                this.infoWindow = new this.googleApi.maps.InfoWindow();
            }
        }
    }

    private applyMapboxTheme(colors: { primary: string, light: string }) {
        if (!this.mapInstance || this.provider !== 'mapbox') return;

        try {
            const layers = this.mapInstance.getStyle().layers;
            if (!layers) return;

            layers.forEach(layer => {
                // Handle background layer
                if (layer.type === 'background') {
                    this.mapInstance.setPaintProperty(layer.id, 'background-color', colors.primary);
                    return;
                }

                // Handle water layers
                if (layer.id.includes('water')) {
                    if (layer.type === 'fill') {
                        this.mapInstance.setPaintProperty(layer.id, 'fill-color', '#ffffff');
                    }
                    return;
                }

                // Handle other fill layers (likely land, landuse, buildings, etc.)
                // If it's not water, we generally want it to be the primary color (land)
                // or transparent if it's an overlay we don't want to emphasize.
                if (layer.type === 'fill') {
                    // Exclude specific layers if needed, but generally color them primary
                    this.mapInstance.setPaintProperty(layer.id, 'fill-color', colors.primary);

                    // Some layers might have opacity set, ensure it's visible or adjusted as needed
                    // this.mapInstance.setPaintProperty(layer.id, 'fill-opacity', 1);
                }

                // Handle text color and halo for symbol layers
                if (layer.type === 'symbol') {
                    this.mapInstance.setPaintProperty(layer.id, 'text-color', '#ffffff');
                    this.mapInstance.setPaintProperty(layer.id, 'text-halo-color', colors.primary);
                    this.mapInstance.setPaintProperty(layer.id, 'text-halo-width', 1); // Ensure halo is visible
                }
            });

        } catch (e) {
            console.warn('Error applying Mapbox theme:', e);
        }
    }

    addMarker(lng: number, lat: number, title?: string, tooltipText?: string) {
        if (!this.mapInstance || !this.provider) return;

        if (this.provider === 'google') {
            const marker = new this.googleApi.maps.Marker({
                position: { lat, lng },
                map: this.mapInstance,
                title,
            });
            if (tooltipText) {
                marker.addListener('click', () => {
                    this.infoWindow.setContent(tooltipText);
                    this.infoWindow.open(this.mapInstance, marker);
                });
            }
            return marker;
        } else if (this.provider === 'mapbox') {
            // Create custom marker element to match Google Maps style (red pin with darker dot)
            const el = document.createElement('div');
            el.className = 'mapbox-custom-marker';
            el.style.width = '27px';
            el.style.height = '43px';
            el.style.backgroundImage = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 27 43'%3E%3Cpath fill='%23EA4335' d='M13.5 0C6.055 0 0 6.055 0 13.5c0 10.125 13.5 29.5 13.5 29.5S27 23.625 27 13.5C27 6.055 20.945 0 13.5 0z'/%3E%3Ccircle cx='13.5' cy='13.5' r='4.5' fill='%23B31412'/%3E%3C/svg%3E")`;
            el.style.backgroundSize = 'contain';
            el.style.backgroundRepeat = 'no-repeat';
            el.style.cursor = 'pointer';

            const marker = new mapboxgl.Marker({ element: el, offset: [0, -21] }) // Offset to align tip with coordinate
                .setLngLat([lng, lat])
                .addTo(this.mapInstance);

            this.mapboxMarkers.push(marker);

            if (tooltipText) {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = tooltipText;

                const popup = new mapboxgl.Popup({ offset: 25 })
                    .setDOMContent(tempDiv);

                marker.setPopup(popup);
            }
            return marker;
        }
    }

    addPolyline(path: { lat: number; lng: number }[], options?: any) {
        if (!this.mapInstance || !this.provider) return;

        if (this.provider === 'google') {
            const finalOptions = { ...options };

            if (options?.iconStyle === 'FORWARD_OPEN_ARROW') {
                const arrow = {
                    path: this.googleApi.maps.SymbolPath.FORWARD_OPEN_ARROW,
                    fillColor: 'gray',
                    fillOpacity: 1,
                    strokeColor: 'gray',
                    strokeWeight: 0.4,
                };
                finalOptions.icons = [
                    {
                        icon: arrow,
                        repeat: options.iconRepeat || '70px',
                    },
                ];
                delete finalOptions.iconStyle;
                delete finalOptions.iconRepeat;
            }

            return new this.googleApi.maps.Polyline({
                ...finalOptions,
                path,
                map: this.mapInstance,
            });
        } else if (this.provider === 'mapbox') {
            let pathPoints = path;
            if (options?.geodesic) {
                pathPoints = this.densifyGeodesicPath(path);
            }
            const coordinates = this.ensureContinuousPath(pathPoints);
            const layerId = `route-${Date.now()}`;
            const arrowLayerId = `${layerId}-arrows`;

            if (!this.mapInstance.hasImage('arrow')) {
                const arrowIcon = this.createArrowIcon();
                this.mapInstance.addImage('arrow', arrowIcon, { sdf: true });
            }

            this.mapInstance.addSource(layerId, {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates,
                    },
                },
            });

            this.mapInstance.addLayer({
                id: layerId,
                type: 'line',
                source: layerId,
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round',
                },
                paint: {
                    'line-color': options?.strokeColor || '#888',
                    'line-width': options?.strokeWeight || 8,
                },
            });

            this.mapInstance.addLayer({
                id: arrowLayerId,
                type: 'symbol',
                source: layerId,
                layout: {
                    'symbol-placement': 'line',
                    'symbol-spacing': 70,
                    'icon-image': 'arrow',
                    'icon-size': 0.5,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                },
                paint: {
                    'icon-color': 'gray',
                }
            });

            this.mapboxLayers.add(layerId);
            this.mapboxLayers.add(arrowLayerId);

            return layerId;
        }
    }

    private densifyGeodesicPath(path: { lat: number; lng: number }[]): { lat: number; lng: number }[] {
        if (!path || path.length < 2) return path;

        const densifiedPath: { lat: number; lng: number }[] = [];

        for (let i = 0; i < path.length - 1; i++) {
            const p1 = path[i];
            const p2 = path[i + 1];

            densifiedPath.push(p1);

            const dist = this.distance(p1.lat, p1.lng, p2.lat, p2.lng);
            const steps = Math.ceil(dist / 50000);

            for (let j = 1; j < steps; j++) {
                const f = j / steps;
                const p = this.intermediatePoint(p1, p2, f);
                densifiedPath.push(p);
            }
        }
        densifiedPath.push(path[path.length - 1]);

        return densifiedPath;
    }

    private distance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371e3;
        const φ1 = lat1 * Math.PI / 180;
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;

        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    }

    private intermediatePoint(p1: { lat: number; lng: number }, p2: { lat: number; lng: number }, f: number): { lat: number; lng: number } {
        const lat1 = p1.lat * Math.PI / 180;
        const lon1 = p1.lng * Math.PI / 180;
        const lat2 = p2.lat * Math.PI / 180;
        const lon2 = p2.lng * Math.PI / 180;

        const d = 2 * Math.asin(Math.min(1, Math.sqrt(Math.pow(Math.sin((lat1 - lat2) / 2), 2) +
            Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin((lon1 - lon2) / 2), 2))));

        if (d < 1e-6) {
            return {
                lat: p1.lat + f * (p2.lat - p1.lat),
                lng: p1.lng + f * (p2.lng - p1.lng)
            };
        }

        const A = Math.sin((1 - f) * d) / Math.sin(d);
        const B = Math.sin(f * d) / Math.sin(d);

        const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
        const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
        const z = A * Math.sin(lat1) + B * Math.sin(lat2);

        const lat = Math.atan2(z, Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2)));
        const lon = Math.atan2(y, x);

        return {
            lat: lat * 180 / Math.PI,
            lng: lon * 180 / Math.PI
        };
    }

    private ensureContinuousPath(path: { lat: number; lng: number }[]): number[][] {
        if (!path || path.length === 0) return [];

        const newPath: number[][] = [];
        let prevLng = path[0].lng;
        newPath.push([prevLng, path[0].lat]);

        for (let i = 1; i < path.length; i++) {
            let lng = path[i].lng;
            const lat = path[i].lat;

            let diff = lng - prevLng;

            while (diff > 180) {
                lng -= 360;
                diff -= 360;
            }
            while (diff < -180) {
                lng += 360;
                diff += 360;
            }

            newPath.push([lng, lat]);
            prevLng = lng;
        }

        return newPath;
    }

    private createArrowIcon() {
        const width = 20;
        const height = 20;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            // Draw a chevron pointing right (>)
            ctx.strokeStyle = '#fff'; // White color for SDF base, will be tinted by icon-color
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(width, height / 2);
            ctx.lineTo(0, height);
            ctx.stroke();
        }
        return ctx.getImageData(0, 0, width, height);
    }

    removeOverlay(overlay: any) {
        if (!this.mapInstance || !this.provider || !overlay) return;

        if (this.provider === 'google') {
            overlay.setMap(null);
        } else if (this.provider === 'mapbox') {
            if (typeof overlay === 'string') {
                const arrowLayerId = `${overlay}-arrows`;

                if (this.mapInstance.getLayer(arrowLayerId)) {
                    this.mapInstance.removeLayer(arrowLayerId);
                    this.mapboxLayers.delete(arrowLayerId);
                }

                if (this.mapInstance.getLayer(overlay)) {
                    this.mapInstance.removeLayer(overlay);
                    this.mapboxLayers.delete(overlay);
                }

                if (this.mapInstance.getSource(overlay)) {
                    this.mapInstance.removeSource(overlay);
                }
            } else if (overlay.remove) {
                overlay.remove();
                const index = this.mapboxMarkers.indexOf(overlay);
                if (index > -1) {
                    this.mapboxMarkers.splice(index, 1);
                }
            }
        }
    }

    destroyMap() {
        if (this.provider === 'mapbox') {
            this.clearMapboxContent();

            this.containerId = null;
            return;
        }

        if (this.mapInstance && this.provider === 'google') {
            this.containerId = null;
            return;
        }

        this.provider = null;
        this.containerId = null;
    }

    private clearMapboxContent() {
        if (!this.mapInstance || this.provider !== 'mapbox') return;

        this.mapboxMarkers.forEach(marker => {
            if (marker && marker.remove) {
                marker.remove();
            }
        });
        this.mapboxMarkers = [];

        const layersToRemove = Array.from(this.mapboxLayers);
        layersToRemove.forEach(id => {
             if (this.mapInstance.getLayer(id)) {
                this.mapInstance.removeLayer(id);
            }
        });

        layersToRemove.forEach(id => {
             if (this.mapInstance.getSource(id)) {
                this.mapInstance.removeSource(id);
            }
        });

        this.mapboxLayers.clear();
    }

    fitBounds(boundsPoints: { lat: number; lng: number }[]) {
        if (!this.mapInstance || !this.provider) return;

        if (this.provider === 'google') {
            const bounds = new this.googleApi.maps.LatLngBounds();
            boundsPoints.forEach(p => bounds.extend(new this.googleApi.maps.LatLng(p.lat, p.lng)));
            this.mapInstance.fitBounds(bounds);
        } else if (this.provider === 'mapbox') {
            // Use ensureContinuousPath to normalize longitudes for Pacific crossing
            const normalizedPoints = this.ensureContinuousPath(boundsPoints);

            const bounds = new mapboxgl.LngLatBounds();
            normalizedPoints.forEach(p => bounds.extend([p[0], p[1]]));
            this.mapInstance.fitBounds(bounds, { padding: 50 });
        }
    }

    private async isUserInChina(): Promise<boolean> {
        try {
            const res = await fetch('https://ipapi.co/json/');
            const data = await res.json();
            return data.country_code === 'CN';
        } catch {
            return navigator.language.toLowerCase().includes('zh');
        }
    }

    private loadScript(src: string, useCallbackForGoogle = false): Promise<void> {
        if (useCallbackForGoogle) {
            if (this.googleApi) {
                return Promise.resolve();
            }
            if (!this.googleMapsPromise) {
                this.googleMapsPromise = new Promise((resolve, reject) => {
                    const callbackName = 'mapApiLoaded';
                    const scriptSrc = `${src}&callback=${callbackName}`;

                    (window as any)[callbackName] = () => {
                        this.googleApi = window.google;
                        resolve();
                        delete (window as any)[callbackName];
                    };

                    const script = document.createElement('script');
                    script.src = scriptSrc;
                    script.async = true;
                    script.defer = true;
                    script.onerror = (err) => {
                        this.googleMapsPromise = null;
                        delete (window as any)[callbackName];
                        reject(err);
                    };
                    document.body.appendChild(script);
                });
            }
            return this.googleMapsPromise;
        }

        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = (err) => reject(err);
            document.body.appendChild(script);
        });
    }

    private loadStylesheet(href: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`link[href="${href}"]`)) {
                resolve();
                return;
            }
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            link.onload = () => resolve();
            link.onerror = (err) => reject(err);
            document.head.appendChild(link);
        });
    }
}
